import { useCallback, useState } from "react";
import { getDefaultColors } from "../apps/constants";
import { ThemeColors } from "../context/types";

const THEME_COLORS_STORAGE_KEY = `${shopify.config.shop}_kefi_theme_colors`;

const useThemeColorsHook = () => {
    const [loading, setLoading] = useState(true);
    const [themeColors, setThemeColors] = useState<ThemeColors | null>(null);

    // Get theme colors from localStorage
    const getStoredThemeColors = useCallback((): ThemeColors | null => {
        try {
            const storedColors = localStorage.getItem(THEME_COLORS_STORAGE_KEY);
            if (storedColors) {
                const parsedColors: ThemeColors = JSON.parse(storedColors);
                const isRGB = Object.values(parsedColors).find((v: string) => v.includes("rgb"));
                if (isRGB) {
                    throw new Error("RGB Invalid color");
                }

                return parsedColors;
            }
        } catch (error) {
            console.error("Error reading theme colors from localStorage:", error);
        }
        return null;
    }, []);

    // Store theme colors in localStorage
    const storeThemeColors = useCallback((colors: ThemeColors | null) => {
        try {
            if (colors) {
                localStorage.setItem(THEME_COLORS_STORAGE_KEY, JSON.stringify(colors));
            }
        } catch (error) {
            console.error("Error storing theme colors to localStorage:", error);
        }
    }, []);

    // Fetching Theme from User's Store
    const fetchThemeColors = useCallback(async (sft: any, user_id: number) => {
        setLoading(true);
        try {
            if (user_id > 1570) {
                //change here
                // if (user_id > 757) {
                const storedColors = getStoredThemeColors();

                if (storedColors) {
                    setThemeColors(storedColors);
                    setLoading(false);
                    return;
                }

                const DOMAIN = shopify.config.shop;
                const STOREFRONT_ACCESS_TOKEN = sft;

                if (!DOMAIN || !STOREFRONT_ACCESS_TOKEN) {
                    setThemeColors(null);
                    return;
                }

                const colors = await getDefaultColors(DOMAIN, STOREFRONT_ACCESS_TOKEN);
                setThemeColors(colors);
                storeThemeColors(colors);
            }
        } catch (error) {
            console.error("Failed to load theme colors:", error);
            setThemeColors(null);
        } finally {
            setLoading(false);
        }
    }, []);

    // Utility function for color fallbacks
    const getColorWithFallback = useCallback((themeColor: string | null | undefined, defaultColor: string) => {
        return themeColor && themeColor !== "" && themeColor !== null ? themeColor : defaultColor;
    }, []);

    // Function to merge design settings with theme colors
    const mergeDesignWithThemeColors = useCallback(
        (designSettings: any) => {
            if (!themeColors) {
                return designSettings;
            }

            return {
                ...designSettings,
                bundle_title_color: getColorWithFallback(themeColors.textColor, designSettings?.bundle_title_color),
                bundle_widget_bg: getColorWithFallback(themeColors.bgColor, designSettings?.bundle_widget_bg),

                product_name_color: getColorWithFallback(themeColors.textColor, designSettings?.product_name_color),
                product_price_color: getColorWithFallback(themeColors.textColor, designSettings?.product_price_color),
                product_img_border_color: getColorWithFallback(
                    themeColors.textColor,
                    designSettings?.product_img_border_color,
                ),
                product_background_color: "#ffffff00", // forced transparent
                product_border_color: getColorWithFallback(themeColors.textColor, designSettings?.product_border_color),

                border_color: getColorWithFallback(themeColors.textColor, designSettings?.border_color),
                background_color: getColorWithFallback(themeColors.bgColor, designSettings?.background_color),

                highlighted_offer_tag_color: getColorWithFallback(
                    themeColors.textColor,
                    designSettings?.highlighted_offer_tag_color,
                ),
                highlighted_price_tag_color: getColorWithFallback(
                    themeColors.textColor,
                    designSettings?.highlighted_price_tag_color,
                ),

                ...(designSettings?.highlighted_offer_tag_background_color && {
                    highlighted_offer_tag_background_color: getColorWithFallback(
                        themeColors.bgColor,
                        designSettings?.highlighted_offer_tag_background_color,
                    ), //con
                }),

                total_footer_text_color: getColorWithFallback(
                    themeColors.textColor,
                    designSettings?.total_footer_text_color,
                ),
                total_footer_price_text_color: getColorWithFallback(
                    themeColors.textColor,
                    designSettings?.total_footer_price_text_color,
                ),
                total_footer_background_color: getColorWithFallback(
                    themeColors.bgColor,
                    designSettings?.total_footer_background_color,
                ),

                variant_selector_text_color: getColorWithFallback(
                    themeColors.textColor,
                    designSettings?.variant_selector_text_color,
                ),

                add_to_cart_bg_color: getColorWithFallback(themeColors.textColor, designSettings?.add_to_cart_bg_color),
                add_to_cart_border_color: getColorWithFallback(
                    themeColors.textColor,
                    designSettings?.add_to_cart_border_color,
                ),
                add_to_cart_text_color: getColorWithFallback(
                    themeColors.bgColor,
                    designSettings?.add_to_cart_text_color,
                ),
                ...(designSettings?.free_gift_header_bg_color && {
                    free_gift_header_bg_color: getColorWithFallback(
                        themeColors.textColor,
                        designSettings?.free_gift_header_bg_color,
                    ), //con
                }),
                ...(designSettings?.free_gift_header_color && {
                    free_gift_header_color: getColorWithFallback(
                        themeColors.bgColor,
                        designSettings?.free_gift_header_color,
                    ), //con
                }),
                ...(designSettings?.free_gift_bg_color && {
                    free_gift_bg_color: getColorWithFallback(themeColors.bgColor, designSettings?.free_gift_bg_color), //con
                }),
                ...(designSettings?.free_gift_text_color && {
                    free_gift_text_color: getColorWithFallback(
                        themeColors.textColor,
                        designSettings?.free_gift_text_color,
                    ), //con
                }),
                ...(designSettings?.free_gift_border_color && {
                    free_gift_border_color: getColorWithFallback(
                        themeColors.textColor,
                        designSettings?.free_gift_border_color,
                    ), //con
                }),
                ...(designSettings?.swatches_select_color && {
                    swatches_select_color: getColorWithFallback(
                        themeColors.textColor,
                        designSettings?.swatches_select_color,
                    ), //con
                }),
                ...(designSettings?.popup_heading_color && {
                    popup_heading_color: getColorWithFallback(themeColors.bgColor, designSettings?.popup_heading_color), //con
                }),
                ...(designSettings?.popup_heading_bg_color && {
                    popup_heading_bg_color: getColorWithFallback(
                        themeColors.textColor,
                        designSettings?.popup_heading_bg_color,
                    ), //con
                }),
                ...(designSettings?.skip_offer_bg_color && {
                    skip_offer_bg_color: getColorWithFallback(themeColors.bgColor, designSettings?.skip_offer_bg_color), //con
                }),
                ...(designSettings?.skip_offer_text_color && {
                    skip_offer_text_color: getColorWithFallback(
                        themeColors.textColor,
                        designSettings?.skip_offer_text_color,
                    ), //con
                }),
                ...(designSettings?.special_text_background_color && {
                    special_text_background_color: getColorWithFallback(
                        themeColors.textColor,
                        designSettings?.special_text_background_color,
                    ), //con
                }),
                ...(designSettings?.special_text_color && {
                    special_text_color: getColorWithFallback(themeColors.bgColor, designSettings?.special_text_color), //con
                }),

                ...(designSettings?.footer_save_money_text_color && {
                    footer_save_money_text_color: getColorWithFallback(
                        themeColors.textColor,
                        designSettings?.footer_save_money_text_color,
                    ), //con
                }),

                ...(designSettings?.discount_label_background_color && {
                    discount_label_background_color: getColorWithFallback(
                        themeColors.textColor,
                        designSettings?.discount_label_background_color,
                    ), //con
                }),
                ...(designSettings?.discount_label_color && {
                    discount_label_color: getColorWithFallback(
                        themeColors.bgColor,
                        designSettings?.discount_label_color,
                    ), //con
                }),

                ...(designSettings?.show_line_around_title_color && {
                    show_line_around_title_color: getColorWithFallback(
                        themeColors.textColor,
                        designSettings?.show_line_around_title_color,
                    ), //con
                }),

                ...(designSettings?.discount_tabs_bg_color && {
                    discount_tabs_bg_color: getColorWithFallback(
                        themeColors.bgColor,
                        designSettings?.discount_tabs_bg_color,
                    ), //con
                }),
                ...(designSettings?.discount_tabs_quantity_label_color && {
                    discount_tabs_quantity_label_color: getColorWithFallback(
                        themeColors.textColor,
                        designSettings?.discount_tabs_quantity_label_color,
                    ), //con
                }),
                ...(designSettings?.discount_tabs_discount_label_color && {
                    discount_tabs_discount_label_color: getColorWithFallback(
                        themeColors.textColor,
                        designSettings?.discount_tabs_discount_label_color,
                    ), //con
                }),

                ...(designSettings?.section_tabs_title_color && {
                    section_tabs_title_color: getColorWithFallback(
                        themeColors.textColor,
                        designSettings?.section_tabs_title_color,
                    ), //con
                }),
                ...(designSettings?.section_tabs_subtitle_color && {
                    section_tabs_subtitle_color: getColorWithFallback(
                        themeColors.textColor,
                        designSettings?.section_tabs_subtitle_color,
                    ), //con
                }),
                ...(designSettings?.section_tabs_bg_color && {
                    section_tabs_bg_color: getColorWithFallback(
                        themeColors.bgColor,
                        designSettings?.section_tabs_bg_color,
                    ), //con
                }),

                ...(designSettings?.custom_field_title_color && {
                    custom_field_title_color: getColorWithFallback(
                        themeColors.textColor,
                        designSettings?.custom_field_title_color,
                    ), //con
                }),
                ...(designSettings?.custom_field_bg_color && {
                    custom_field_bg_color: getColorWithFallback(
                        themeColors.bgColor,
                        designSettings?.custom_field_bg_color,
                    ), //con
                }),
                ...(designSettings?.custom_field_message_color && {
                    custom_field_message_color: getColorWithFallback(
                        themeColors.textColor,
                        designSettings?.custom_field_message_color,
                    ), //con
                }),
            };
        },
        [themeColors, getColorWithFallback],
    );

    return {
        themeColors,
        setThemeColors,
        loading,
        fetchThemeColors,
        getColorWithFallback,
        mergeDesignWithThemeColors,
    };
};

export default useThemeColorsHook;
